// Revision history of a page (design §10): applied revisions newest first
// with author (person, or agent + run link) and change note; pick two to
// diff; restore an older one. Revision list responses may come without
// content — then `onNeedContent` asks the parent to load the selected ones.

import { useEffect, useMemo, useState } from 'react'
import { EmptyState } from '../ui'
import { KbDiff } from './kb-diff'
import { KbAuthorLine } from './kb-page-view'
import type { KbRevisionSummary } from './kb-types'

export interface KbHistoryProps {
  /** Revisions of the page, any order; only applied ones are listed (newest first). */
  revisions: KbRevisionSummary[]
  currentRev: number
  /** Full text (`raw`) per revision id, as far as loaded. */
  raws: Record<string, string | undefined>
  /** Called with revision ids whose text is missing but needed for the diff. */
  onNeedContent?: (revisionIds: string[]) => void
  /** Restore = write this snapshot as a new revision (§6.1). */
  onRestore?: (revision: KbRevisionSummary) => void
  restoringId?: string | null
}

export function KbHistory({
  revisions,
  currentRev,
  raws,
  onNeedContent,
  onRestore,
  restoringId,
}: KbHistoryProps) {
  const sorted = useMemo(
    () =>
      revisions
        .filter((revision) => revision.status === 'applied')
        .sort((x, y) => (y.rev ?? 0) - (x.rev ?? 0)),
    [revisions]
  )
  // Default comparison: the newest revision against the one before it.
  const [toId, setToId] = useState<string | null>(null)
  const [fromId, setFromId] = useState<string | null>(null)
  const to = sorted.find((r) => r.id === toId) ?? sorted[0] ?? null
  const from =
    sorted.find((r) => r.id === fromId) ??
    sorted[sorted.findIndex((r) => r.id === to?.id) + 1] ??
    null

  const missing = [from, to]
    .filter((r): r is KbRevisionSummary => r != null && raws[r.id] == null)
    .map((r) => r.id)
  const missingKey = missing.join(',')
  useEffect(() => {
    if (missing.length) onNeedContent?.(missing)
  }, [missingKey]) // eslint-disable-line react-hooks/exhaustive-deps

  if (sorted.length === 0) {
    return <EmptyState title="No history yet" art={null} />
  }

  const before = from ? (raws[from.id] ?? null) : ''
  const after = to ? (raws[to.id] ?? null) : null

  return (
    <div className="space-y-4">
      <ol className="divide-y divide-line rounded-panel border border-line">
        {sorted.map((revision) => {
          const isTo = revision.id === to?.id
          const isFrom = revision.id === from?.id
          return (
            <li
              key={revision.id}
              className={`flex flex-wrap items-start gap-3 px-4 py-3 ${isTo || isFrom ? 'bg-sunken/60' : ''}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-base text-ink">
                  <span className="font-medium">rev {revision.rev}</span>
                  {revision.rev === currentRev ? (
                    <span className="ml-2 rounded-full bg-moss-soft px-2 text-xs font-medium text-moss">
                      current
                    </span>
                  ) : null}
                  {revision.deleted ? (
                    <span className="ml-2 rounded-full bg-danger-soft px-2 text-xs font-medium text-danger">
                      deleted
                    </span>
                  ) : null}
                  {revision.message ? (
                    <span className="text-ink-2"> — {revision.message}</span>
                  ) : null}
                </p>
                <p className="mt-0.5 text-sm text-ink-3">
                  <KbAuthorLine author={revision.author} at={revision.createdAt} />
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <CompareToggle
                  label="Old"
                  active={isFrom}
                  disabled={isTo}
                  onClick={() => setFromId(revision.id)}
                />
                <CompareToggle
                  label="New"
                  active={isTo}
                  disabled={isFrom}
                  onClick={() => setToId(revision.id)}
                />
                {onRestore && revision.rev !== currentRev && !revision.deleted ? (
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => onRestore(revision)}
                    disabled={restoringId != null}
                  >
                    {restoringId === revision.id ? 'Restoring…' : 'Restore'}
                  </button>
                ) : null}
              </div>
            </li>
          )
        })}
      </ol>

      {to ? (
        before == null || after == null ? (
          <p className="text-base text-ink-3">Loading revisions…</p>
        ) : (
          <KbDiff
            before={before}
            after={after}
            beforeLabel={from ? `rev ${from.rev}` : 'empty'}
            afterLabel={`rev ${to.rev}`}
          />
        )
      ) : null}
    </div>
  )
}

function CompareToggle({
  label,
  active,
  disabled,
  onClick,
}: {
  label: string
  active: boolean
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      disabled={disabled}
      title={`Compare as ${label.toLowerCase()} revision`}
      className={`rounded-control border px-2 py-0.5 text-xs font-medium transition-colors disabled:opacity-40 ${
        active
          ? 'border-moss bg-moss-soft text-moss'
          : 'border-line-strong text-ink-3 hover:text-ink'
      }`}
    >
      {label}
    </button>
  )
}
